/**
 * Task Brief Modification Modal
 *
 * Allows users to edit task brief fields and answer clarification questions.
 * Sends modifications to the backend for AI re-analysis.
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  Plus,
  Trash2,
  Save,
  AlertCircle,
  CheckCircle,
  Target,
  FileText,
  HelpCircle,
  Loader
} from 'lucide-react';
import { useTaskBriefModification } from '../hooks/useTaskBrief';

export default function TaskBriefModifyModal({ taskBriefData, onClose, onModified }) {
  const briefId = taskBriefData.task_brief_id || taskBriefData.taskBriefId;
  const brief = taskBriefData.iteration?.brief || taskBriefData.brief;

  const { modifying, error, modify } = useTaskBriefModification(briefId);

  // Editable state
  const [taskSummary, setTaskSummary] = useState(brief.task_summary || '');
  const [objectives, setObjectives] = useState(brief.objectives || []);
  const [inScope, setInScope] = useState(brief.task_scope?.in_scope || []);
  const [outOfScope, setOutOfScope] = useState(brief.task_scope?.out_of_scope || []);
  const [assumptions, setAssumptions] = useState(brief.task_scope?.assumptions || []);
  const [successCriteria, setSuccessCriteria] = useState(brief.success_criteria || []);
  const [userNotes, setUserNotes] = useState('');

  // Clarification question answers
  const [questionAnswers, setQuestionAnswers] = useState({});
  useEffect(() => {
    const initial = {};
    if (brief.clarification_questions) {
      brief.clarification_questions.forEach((q, idx) => {
        initial[idx] = q.suggested_answer || '';
      });
    }
    setQuestionAnswers(initial);
  }, [brief.clarification_questions]);

  // Active tab
  const [activeTab, setActiveTab] = useState('summary');

  const handleAddItem = (list, setList) => {
    setList([...list, '']);
  };

  const handleUpdateItem = (list, setList, index, value) => {
    const updated = [...list];
    updated[index] = value;
    setList(updated);
  };

  const handleRemoveItem = (list, setList, index) => {
    setList(list.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    // Build modifications object
    const modifications = {};

    if (taskSummary !== brief.task_summary) {
      modifications.task_summary = taskSummary;
    }

    if (JSON.stringify(objectives) !== JSON.stringify(brief.objectives)) {
      modifications.objectives = objectives.filter(o => o.trim());
    }

    const scopeChanges = {};
    if (JSON.stringify(inScope) !== JSON.stringify(brief.task_scope?.in_scope || [])) {
      scopeChanges.in_scope = inScope.filter(s => s.trim());
    }
    if (JSON.stringify(outOfScope) !== JSON.stringify(brief.task_scope?.out_of_scope || [])) {
      scopeChanges.out_of_scope = outOfScope.filter(s => s.trim());
    }
    if (JSON.stringify(assumptions) !== JSON.stringify(brief.task_scope?.assumptions || [])) {
      scopeChanges.assumptions = assumptions.filter(a => a.trim());
    }
    if (Object.keys(scopeChanges).length > 0) {
      modifications.task_scope = {
        ...brief.task_scope,
        ...scopeChanges
      };
    }

    if (JSON.stringify(successCriteria) !== JSON.stringify(brief.success_criteria || [])) {
      modifications.success_criteria = successCriteria.filter(c => c.trim());
    }

    // Include answered clarification questions
    const answeredQuestions = {};
    Object.entries(questionAnswers).forEach(([idx, answer]) => {
      if (answer && answer.trim()) {
        const question = brief.clarification_questions[parseInt(idx)];
        answeredQuestions[question.question] = answer;
      }
    });
    if (Object.keys(answeredQuestions).length > 0) {
      modifications.clarification_answers = answeredQuestions;
    }

    if (Object.keys(modifications).length === 0) {
      alert('No changes detected. Please modify something before submitting.');
      return;
    }

    // Submit modifications
    const result = await modify(modifications, userNotes || null);
    if (result && onModified) {
      onModified(result);
    }
  };

  const tabs = [
    { id: 'summary', label: 'Summary', icon: FileText },
    { id: 'objectives', label: 'Objectives', icon: Target },
    { id: 'scope', label: 'Scope', icon: CheckCircle },
    { id: 'success', label: 'Success Criteria', icon: CheckCircle },
    ...(brief.clarification_questions && brief.clarification_questions.length > 0
      ? [{ id: 'questions', label: 'Answer Questions', icon: HelpCircle }]
      : [])
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-blue-600 text-white p-6 rounded-t-lg flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Modify Task Understanding</h2>
            <p className="text-blue-100 text-sm mt-1">
              Edit the brief or answer questions. AI will re-analyze with your changes.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-700 rounded-lg p-2 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50 px-6">
          <div className="flex gap-2 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Summary Tab */}
          {activeTab === 'summary' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task Summary
                </label>
                <textarea
                  value={taskSummary}
                  onChange={(e) => setTaskSummary(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={3}
                  placeholder="Concise one-sentence summary of the task..."
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  <AlertCircle size={16} className="inline mr-2" />
                  Edit the summary to better reflect your task's main objective.
                </p>
              </div>
            </div>
          )}

          {/* Objectives Tab */}
          {activeTab === 'objectives' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Task Objectives
                </label>
                <button
                  onClick={() => handleAddItem(objectives, setObjectives)}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Plus size={16} />
                  Add Objective
                </button>
              </div>
              <div className="space-y-3">
                {objectives.map((obj, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      type="text"
                      value={obj}
                      onChange={(e) => handleUpdateItem(objectives, setObjectives, idx, e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Specific, measurable objective..."
                    />
                    <button
                      onClick={() => handleRemoveItem(objectives, setObjectives, idx)}
                      className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
                {objectives.length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    No objectives yet. Click "Add Objective" to create one.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Scope Tab */}
          {activeTab === 'scope' && (
            <div className="space-y-6">
              {/* In Scope */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-green-700 flex items-center gap-2">
                    <CheckCircle size={16} />
                    In Scope
                  </label>
                  <button
                    onClick={() => handleAddItem(inScope, setInScope)}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Add
                  </button>
                </div>
                <div className="space-y-2">
                  {inScope.map((item, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => handleUpdateItem(inScope, setInScope, idx, e.target.value)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="What's included..."
                      />
                      <button
                        onClick={() => handleRemoveItem(inScope, setInScope, idx)}
                        className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Out of Scope */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-red-700 flex items-center gap-2">
                    <X size={16} />
                    Out of Scope
                  </label>
                  <button
                    onClick={() => handleAddItem(outOfScope, setOutOfScope)}
                    className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Add
                  </button>
                </div>
                <div className="space-y-2">
                  {outOfScope.map((item, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => handleUpdateItem(outOfScope, setOutOfScope, idx, e.target.value)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="What's excluded..."
                      />
                      <button
                        onClick={() => handleRemoveItem(outOfScope, setOutOfScope, idx)}
                        className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Assumptions */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Assumptions
                  </label>
                  <button
                    onClick={() => handleAddItem(assumptions, setAssumptions)}
                    className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Add
                  </button>
                </div>
                <div className="space-y-2">
                  {assumptions.map((item, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => handleUpdateItem(assumptions, setAssumptions, idx, e.target.value)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Assumption..."
                      />
                      <button
                        onClick={() => handleRemoveItem(assumptions, setAssumptions, idx)}
                        className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Success Criteria Tab */}
          {activeTab === 'success' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Success Criteria
                </label>
                <button
                  onClick={() => handleAddItem(successCriteria, setSuccessCriteria)}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Plus size={16} />
                  Add Criterion
                </button>
              </div>
              <div className="space-y-3">
                {successCriteria.map((criterion, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      type="text"
                      value={criterion}
                      onChange={(e) => handleUpdateItem(successCriteria, setSuccessCriteria, idx, e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="How to verify task completion..."
                    />
                    <button
                      onClick={() => handleRemoveItem(successCriteria, setSuccessCriteria, idx)}
                      className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
                {successCriteria.length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    No success criteria yet. Click "Add Criterion" to create one.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Questions Tab */}
          {activeTab === 'questions' && brief.clarification_questions && (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-900">
                  <HelpCircle size={16} className="inline mr-2" />
                  Answer these questions to help AI better understand your task.
                  Leaving them blank will use the suggested answers.
                </p>
              </div>
              {brief.clarification_questions.map((q, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="font-medium text-gray-900 mb-2">{q.question}</div>
                  <div className="text-sm text-gray-600 mb-3">
                    <strong>Why asking:</strong> {q.why_asking}
                  </div>
                  {q.suggested_answer && (
                    <div className="text-sm text-gray-500 mb-2">
                      <strong>Suggested:</strong> {q.suggested_answer}
                    </div>
                  )}
                  <textarea
                    value={questionAnswers[idx] || ''}
                    onChange={(e) => setQuestionAnswers({
                      ...questionAnswers,
                      [idx]: e.target.value
                    })}
                    placeholder="Your answer (optional - will use suggested answer if blank)..."
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                </div>
              ))}
            </div>
          )}

          {/* User Notes */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes for AI (Optional)
            </label>
            <textarea
              value={userNotes}
              onChange={(e) => setUserNotes(e.target.value)}
              placeholder="Explain why you're making these changes..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="px-6 pb-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              <AlertCircle size={18} className="inline mr-2" />
              {error.message}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 flex gap-3">
          <button
            onClick={handleSubmit}
            disabled={modifying}
            className="flex-1 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {modifying ? (
              <>
                <Loader size={18} className="animate-spin" />
                <span>Re-analyzing...</span>
              </>
            ) : (
              <>
                <Save size={18} />
                <span>Save & Re-analyze</span>
              </>
            )}
          </button>
          <button
            onClick={onClose}
            disabled={modifying}
            className="px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
